import { useMemo, useState } from 'react'
import { PageShell } from '@components/common/PageShell'
import { ResponsiveTabs } from '@components/common/ResponsiveTabs'
import { useAuthStore } from '@store/authStore'
import {
  BadgeCategoryListTable,
  BadgeRarityModerationTable,
  SystemAchievementListTable,
} from '../components/badges'

/**
 * AdminBadgesPage
 *
 * Three tabs:
 *  - "Categories"        — Admin only (BadgeCategory CRUD)
 *  - "Achievements"      — Admin only (the platform's own badges, which had no screen at all
 *                          until now: they were created by calling the Admin API by hand)
 *  - "Rarity Moderation" — Admin AND Moderator
 *
 * Role gating: the route allows Admin + Moderator (see router/index.tsx).
 * Moderators who are NOT admins only see the Rarity Moderation tab; the
 * Categories tab is hidden and never rendered for them.
 */
export default function AdminBadgesPage() {
  const { user } = useAuthStore()

  const isAdmin = !!user?.roles?.includes('Admin')

  // Tabs available to this user. Categories and Achievements are Admin-only.
  const tabs = useMemo(() => {
    const list: { key: string; label: string }[] = []
    if (isAdmin) {
      list.push({ key: 'categories', label: 'Categories' })
      list.push({ key: 'achievements', label: 'Achievements' })
    }
    list.push({ key: 'moderation', label: 'Rarity Moderation' })
    return list
  }, [isAdmin])

  const [tab, setTab] = useState(tabs[0]?.key ?? 'moderation')
  const currentKey = tabs.some((t) => t.key === tab) ? tab : (tabs[0]?.key ?? 'moderation')

  return (
    <PageShell title="Badges">
      <ResponsiveTabs tabs={tabs} value={currentKey} onChange={setTab} sx={{ mb: 2 }} />

      {currentKey === 'categories' && <BadgeCategoryListTable />}
      {/* The earned-dates repair is a toolbar button inside the list, so the list comes first. */}
      {currentKey === 'achievements' && <SystemAchievementListTable />}
      {currentKey === 'moderation' && <BadgeRarityModerationTable />}
    </PageShell>
  )
}
